import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const SentenceGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffledWords, setShuffledWords] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const shuffleWords = useCallback((sentence: string) => {
    const words = sentence.replace(/[.!?]/g, '').split(' ').filter(w => w.length > 0);
    const shuffled = [...words].sort(() => Math.random() - 0.5);
    // Ensure it's actually shuffled
    if (shuffled.join(' ') === words.join(' ') && words.length > 1) {
      shuffled.reverse();
    }
    return shuffled;
  }, []);

  useEffect(() => {
    const loadItems = async () => {
      try {
        // Get more content to filter for sentences only
        const data = await getRandomContent(50, lessonId, customSetId);
        // Filter for actual sentences: must have 3+ words (real sentences, not compound words)
        const sentences = data.filter(item => {
          const wordCount = item.english.trim().split(/\s+/).length;
          return wordCount >= 3;
        });
        setItems(sentences.slice(0, 10));
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  useEffect(() => {
    if (items.length > 0 && currentIndex < items.length) {
      setShuffledWords(shuffleWords(items[currentIndex].english));
      setSelectedWords([]);
    }
  }, [items, currentIndex, shuffleWords]);

  const handleWordClick = (word: string, index: number) => {
    if (feedback) return;
    setSelectedWords([...selectedWords, word]);
    setShuffledWords(shuffledWords.filter((_, i) => i !== index));
  };

  const handleSelectedWordClick = (word: string, index: number) => {
    if (feedback) return;
    setShuffledWords([...shuffledWords, word]);
    setSelectedWords(selectedWords.filter((_, i) => i !== index));
  };

  const handleCheck = async () => {
    const currentItem = items[currentIndex];
    const userAnswer = selectedWords.join(' ');
    const correctAnswer = currentItem.english.replace(/[.!?]/g, '');
    const isCorrect = userAnswer.toLowerCase() === correctAnswer.toLowerCase();

    setFeedback(isCorrect ? 'correct' : 'wrong');
    await recordProgress(currentItem.id, isCorrect);
    
    speak(currentItem.english);

    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setFeedback(null);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (score.correct + (feedback === 'correct' ? 1 : 0) === items.length) {
      await awardBonus(30, 'perfect_sentence');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało zdań!</h2>
        <p>Wybierz lekcję ze zdaniami.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+30 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>📝 Ułóż zdanie</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="sentence-card">
        <div className="polish-word">{currentItem.polish}</div>
        <p className="quiz-instruction">Ułóż słowa w poprawnej kolejności:</p>

        <div className={`sentence-answer-area ${feedback || ''}`}>
          {selectedWords.length === 0 ? (
            <span className="placeholder-text">Kliknij słowa poniżej...</span>
          ) : (
            selectedWords.map((word, index) => (
              <button
                key={index}
                className="word-chip selected"
                onClick={() => handleSelectedWordClick(word, index)}
                disabled={!!feedback}
              >
                {word}
              </button>
            ))
          )}
        </div>

        <div className="word-bank">
          {shuffledWords.map((word, index) => (
            <button
              key={index}
              className="word-chip"
              onClick={() => handleWordClick(word, index)}
              disabled={!!feedback}
            >
              {word}
            </button>
          ))}
        </div>

        {feedback === 'wrong' && (
          <div className="correct-answer">
            Poprawna odpowiedź: <strong>{currentItem.english}</strong>
            <button className="speak-btn-inline" onClick={() => speak(currentItem.english)}>🔊</button>
          </div>
        )}

        {!feedback && shuffledWords.length === 0 && (
          <button className="btn btn-primary" onClick={handleCheck}>
            Sprawdź ➜
          </button>
        )}

        {feedback && (
          <button className="btn btn-primary btn-next" onClick={handleNext}>
            Dalej ➜
          </button>
        )}
      </div>
    </div>
  );
};

export default SentenceGame;

